/**
 * Um select que é também o filtro: um campo só.
 *
 * Clicar abre a lista com todas as opções; digitar filtra a lista logo abaixo.
 * Substitui o par "input de filtro + select nativo" que se espalhou pelas telas,
 * onde a pessoa digitava no filtro sem ver o resultado até abrir o select.
 *
 * Os catálogos vão a 1.663 itens: a lista desenha no máximo 200 e diz quantos
 * ficaram de fora, como o resto do sistema. A ordem é a de `itens` — quem chama
 * ordena, se precisar.
 *
 * O texto casa por palavra, sem caixa e sem acento ("rele bomba" acha "Relé da
 * bomba"). Quando `itens` é só a primeira página de algo maior, `buscar` leva o
 * termo ao servidor — 2 caracteres e 400 ms, a regra 2 —, senão o que ficou
 * fora da página nunca apareceria.
 *
 * Teclado: ↑ ↓ andam na lista, Enter escolhe, Esc fecha.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { BUSCA_ESPERA_MS, BUSCA_MINIMO_CARACTERES } from "@/api/query"
import { casaBusca } from "./buscaDeTexto"

export type OpcaoDoSeletor = { id: number; rotulo: string }

/** A busca no servidor, para quando `itens` não é a lista inteira. */
export type BuscaDoSeletor = {
  /** Identifica a lista no cache (o projeto, o catálogo). */
  chave: readonly unknown[]
  /** Quantos existem ao todo — `itens` traz só parte. */
  total: number
  consultar: (termo: string) => Promise<OpcaoDoSeletor[]>
}

const MAXIMO_DESENHADO = 200

export function SeletorComBusca({
  itens, valor, aoMudar, carregando, rotuloVazio, placeholder, rotuloAcessivel, buscar,
  maximoDesenhado = MAXIMO_DESENHADO,
}: {
  itens: OpcaoDoSeletor[]
  /** O id escolhido; 0 = nada escolhido. */
  valor: number
  aoMudar: (id: number) => void
  carregando?: boolean
  /**
   * Presente, a lista começa com uma opção que volta o valor para 0 — o
   * "Todos" de um filtro. Ausente, não há como desescolher pela lista.
   */
  rotuloVazio?: string
  placeholder?: string
  /** Nome do campo para leitor de tela, quando o rótulo visível não está num `<label>` ligado a ele. */
  rotuloAcessivel?: string
  /** Presente quando `itens` é só parte da lista: o termo digitado vai ao servidor. */
  buscar?: BuscaDoSeletor
  /**
   * Quantas opções desenhar antes de resumir. O padrão é o corte do sistema (200); quem
   * rola a lista procurando um nome sem digitar precisa de todas — ver `Escolha`.
   */
  maximoDesenhado?: number
}) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState("")
  const [marcado, setMarcado] = useState(0)
  const listaRef = useRef<HTMLUListElement>(null)

  // O termo que vai ao servidor espera a digitação parar.
  const [termoServidor, setTermoServidor] = useState("")
  useEffect(() => {
    const t = setTimeout(() => setTermoServidor(busca.trim()), BUSCA_ESPERA_MS)
    return () => clearTimeout(t)
  }, [busca])
  const noServidor = buscar != null && buscar.total > itens.length
    && termoServidor.length >= BUSCA_MINIMO_CARACTERES
  const doServidor = useQuery({
    queryKey: [...(buscar?.chave ?? []), "seletor-busca", termoServidor] as const,
    enabled: noServidor,
    staleTime: 60_000,
    queryFn: () => buscar!.consultar(termoServidor),
  })

  // O escolhido pela busca no servidor pode não estar em `itens`; o campo
  // fechado ainda precisa do nome dele.
  const conhecidos = useRef(new Map<number, OpcaoDoSeletor>())
  for (const x of itens) conhecidos.current.set(x.id, x)
  for (const x of doServidor.data ?? []) conhecidos.current.set(x.id, x)

  const achados = useMemo(() => {
    if (noServidor && doServidor.data) return doServidor.data
    return busca.trim() ? itens.filter((x) => casaBusca(busca, x.rotulo)) : itens
  }, [itens, busca, noServidor, doServidor.data])
  const procurando = carregando || (noServidor && doServidor.isFetching)

  // A opção vazia só aparece sem busca: quem está digitando procura um item.
  const opcoes: OpcaoDoSeletor[] = [
    ...(rotuloVazio && !busca.trim() ? [{ id: 0, rotulo: rotuloVazio }] : []),
    ...achados.slice(0, maximoDesenhado),
  ]
  const foraDaLista = achados.length - Math.min(achados.length, maximoDesenhado)
  const escolhido = valor > 0 ? conhecidos.current.get(valor) : undefined
  const textoFechado = escolhido?.rotulo ?? (valor === 0 && rotuloVazio ? rotuloVazio : "")

  function abrir() {
    // Abre sempre com a lista inteira: quem clica quer ver as opções, e filtrar
    // pelo nome já escolhido esconderia todas as outras.
    setBusca("")
    setMarcado(0)
    setAberto(true)
  }

  function fechar() {
    setAberto(false)
    setBusca("")
  }

  function escolher(opcao: OpcaoDoSeletor) {
    aoMudar(opcao.id)
    fechar()
  }

  function marcar(i: number) {
    setMarcado(i)
    listaRef.current?.children[i]?.scrollIntoView({ block: "nearest" })
  }

  function aoTeclar(ev: React.KeyboardEvent<HTMLInputElement>) {
    if (ev.key === "ArrowDown") {
      ev.preventDefault()
      if (!aberto) abrir()
      else marcar(Math.min(marcado + 1, opcoes.length - 1))
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault()
      marcar(Math.max(marcado - 1, 0))
    } else if (ev.key === "Enter" && aberto) {
      ev.preventDefault()
      const opcao = opcoes[marcado]
      if (opcao) escolher(opcao)
    } else if (ev.key === "Escape" && aberto) {
      ev.preventDefault()
      fechar()
    }
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        role="combobox"
        aria-label={rotuloAcessivel}
        aria-expanded={aberto}
        aria-autocomplete="list"
        // Aberto, o campo é a busca; fechado, mostra o que foi escolhido.
        value={aberto ? busca : textoFechado}
        placeholder={carregando ? "Carregando…" : (textoFechado || placeholder || "Escolha ou digite para filtrar…")}
        onFocus={abrir}
        onClick={() => { if (!aberto) abrir() }}
        onBlur={fechar}
        onChange={(e) => { setBusca(e.target.value); setMarcado(0); setAberto(true) }}
        onKeyDown={aoTeclar}
        style={{
          width: "100%", boxSizing: "border-box", padding: "5px 24px 5px 8px", fontSize: 12,
          border: "1px solid " + (aberto ? "var(--acao)" : "var(--line)"), borderRadius: 5,
          background: "var(--surface)", color: "var(--txt)",
        }}
      />
      <span aria-hidden style={{
        position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
        fontSize: 10, color: "var(--txt3)", pointerEvents: "none",
      }}>
        ▾
      </span>

      {aberto && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 20,
          border: "1px solid var(--line)", borderRadius: 6, background: "var(--surface)",
          boxShadow: "var(--sombra, 0 4px 12px rgba(0,0,0,.12))",
        }}>
          <ul ref={listaRef} role="listbox" style={{
            listStyle: "none", margin: 0, padding: 4, maxHeight: 260, overflowY: "auto",
          }}>
            {opcoes.map((x, i) => (
              <li
                key={x.id}
                role="option"
                aria-selected={i === marcado}
                // mouseDown com preventDefault: escolhe antes do blur do campo fechar a lista.
                onMouseDown={(e) => { e.preventDefault(); escolher(x) }}
                onMouseEnter={() => setMarcado(i)}
                style={{
                  padding: "4px 8px", fontSize: 12, borderRadius: 4, cursor: "pointer",
                  background: i === marcado ? "var(--surface2)" : "transparent",
                  fontWeight: x.id === valor ? 600 : 400,
                  color: x.id === 0 ? "var(--txt2)" : undefined,
                }}
              >
                {x.rotulo}
              </li>
            ))}
          </ul>
          {procurando && (
            <p style={{ margin: 0, padding: "6px 10px", fontSize: 11.5, color: "var(--txt3)" }}>Carregando…</p>
          )}
          {!procurando && achados.length === 0 && (
            <p style={{ margin: 0, padding: "6px 10px", fontSize: 11.5, color: "var(--txt3)" }}>
              Nenhuma opção com esse texto.
            </p>
          )}
          {/* A lista é só a primeira página: dizer, em vez de deixar parecer que é tudo. */}
          {buscar && buscar.total > itens.length && !noServidor && (
            <p style={{
              margin: 0, padding: "5px 10px", fontSize: 11, color: "var(--txt3)",
              borderTop: "1px solid var(--line2)",
            }}>
              Mostrando {itens.length} de {buscar.total}. Digite para buscar em todos.
            </p>
          )}
          {foraDaLista > 0 && (
            <p style={{
              margin: 0, padding: "5px 10px", fontSize: 11, color: "var(--txt3)",
              borderTop: "1px solid var(--line2)",
            }}>
              {achados.length} opções; mostrando {maximoDesenhado}. Digite para filtrar.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
